import React, { useState, useEffect } from "react";
import { User } from "../types/user";
import { Org, Workspace } from "../types/org";
import TabGPT from "../tabs/TabGPT";
import Icon1 from "../icons/Icon";
import Space from "../layout/Space";
import SearchBarDocuments from "../textfields/SearchBarDocuments";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import Modal1 from "../modals/Modal1";
import DataGridGPT2 from "../tables/DataGridGPT2";
import { queryCollection } from "../models/DataFunctions";
import SmallSpinner from "../loading/SmallSpinner";
import AddEquipmentToBookshelfModal from "../modals/AddEquipmentToBookshelfModal";
import { EquipmentModal } from "../modals/EquipmentModal";
import addEquipmentToBookshelf from "../api/equipment/addEquipmentToBookshelf";
import deleteDocApi from "../api/database/deleteDocApi";
import { doc } from "firebase/firestore";
import { db } from "../config/FirebaseConfig";
import { useAppContext } from "../config/AppContext";
import AddPartnerFicheModal from "../modals/AddPartnerFicheModal";
import createDocApi from "../api/database/createDocApi";

interface BibliothequeProps {
  userData: User;
  orgData: Org;
  navigateWorkspace: (path: string) => void;
  workspaceData?: Workspace;
  overrideId?: string;
}

function Bibliotheque({
  userData,
  orgData,
  navigateWorkspace,
  workspaceData,
  overrideId,
}: BibliothequeProps) {
  const [activeTab, setActiveTab] = useState("equipements");
  const [searchValue, setSearchValue] = useState("");
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false);
  const [equipmentData, setEquipmentData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingEquipment, setSubmittingEquipment] = useState(false);
  const [deletingEquipment, setDeletingEquipment] = useState<string | null>(null);
  const [equipmentToDelete, setEquipmentToDelete] = useState<any>(null);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);

  // Partner fiches state
  const [partnerData, setPartnerData] = useState<any[]>([]);
  const [isAddPartnerModalOpen, setIsAddPartnerModalOpen] = useState(false);
  const [submittingPartner, setSubmittingPartner] = useState(false);
  const [deletingPartner, setDeletingPartner] = useState<string | null>(null);
  const [partnerToDelete, setPartnerToDelete] = useState<any>(null);
  const [editingPartner, setEditingPartner] = useState<any>(null);

  const { appState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const tabs = [
    { label: "Équipements", id: "equipements" },
    ...(orgData?.logement?.access?.can_create === true ? [{ label: "Fiches partenaires", id: "partenaires" }] : []),
  ];

  const handleFetchData = async () => {
    if (!orgData?.id) return;

    setLoading(true);
    try {
      // Fetch equipment
      const equipmentFilters = [
        { field: "org_id", operator: "==", value: orgData.id },
        { field: "type", operator: "==", value: "bookshelf_equipment" }
      ];
      const equipment = await queryCollection("logement_bookshelf", equipmentFilters);
      setEquipmentData(equipment || []);

      // Fetch partner fiches
      const partnerFilters = [
        { field: "org_id", operator: "==", value: orgData.id },
        { field: "type", operator: "==", value: "bookshelf_partner" }
      ];
      const partners = await queryCollection("logement_bookshelf", partnerFilters);
      setPartnerData(partners || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setEquipmentData([]);
      setPartnerData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipment = () => {
    setIsAddEquipmentModalOpen(true);
  };

  const handleSubmitEquipment = async (equipmentData: any) => {
    setSubmittingEquipment(true);
    try {
      const dataToSubmit = {
        ...equipmentData,
        type: "equipment" // Add the type field required by the filter
      };
      
      const response = await addEquipmentToBookshelf(dataToSubmit, prefix);
      setIsAddEquipmentModalOpen(false);
      await handleFetchData(); // Refresh the list
      
      // Auto-open EquipmentModal with the newly created equipment
      if (response && response.id) {
        setSelectedEquipmentId(response.id);
        setIsEquipmentModalOpen(true);
      }
    } catch (error) {
      console.error("Error adding equipment:", error);
      alert("Erreur lors de l'ajout de l'équipement");
    } finally {
      setSubmittingEquipment(false);
    }
  };


  const handleDeleteEquipment = (equipment: any) => {
    setEquipmentToDelete(equipment);
  };

  const confirmDeleteEquipment = async () => {
    if (!equipmentToDelete) return;
    
    setDeletingEquipment(equipmentToDelete.id);
    try {
      // Create document reference for deletion
      const equipmentRef = doc(db, "logement_bookshelf", equipmentToDelete.id);
      await deleteDocApi(equipmentRef, prefix);
      
      // Refresh the equipment list
      await handleFetchData();
      setEquipmentToDelete(null);
    } catch (error) {
      console.error("Error deleting equipment:", error);
      alert("Erreur lors de la suppression de l'équipement");
    } finally {
      setDeletingEquipment(null);
    }
  };

  const cancelDeleteEquipment = () => {
    setEquipmentToDelete(null);
  };

  const handleEquipmentClick = (equipmentId: string) => {
    setSelectedEquipmentId(equipmentId);
    setIsEquipmentModalOpen(true);
  };

  const handleCloseEquipmentModal = () => {
    setIsEquipmentModalOpen(false);
    setSelectedEquipmentId(null);
    setSearchValue(""); // Reset search to show all equipments
  };

  const handleRefreshEquipment = () => {
    handleFetchData(); // Refresh the equipment list
  };

  // Partner fiches handlers
  const handleAddPartner = () => {
    setEditingPartner(null);
    setIsAddPartnerModalOpen(true);
  };

  const handleEditPartner = (partner: any) => {
    setEditingPartner(partner);
    setIsAddPartnerModalOpen(true);
  };

  const handleSubmitPartner = async (partnerData: any) => {
    setSubmittingPartner(true);
    try {
      if (editingPartner) {
        // Update existing partner
        const partnerRef = doc(db, "logement_bookshelf", editingPartner.id);
        await deleteDocApi(partnerRef, prefix);

        // Create new document with updated data
        const dataToSubmit = {
          ...partnerData,
          org_id: orgData.id,
          type: "bookshelf_partner"
        };
        await createDocApi("logement_bookshelf", dataToSubmit, prefix);
      } else {
        // Create new partner
        const dataToSubmit = {
          ...partnerData,
          org_id: orgData.id,
          type: "bookshelf_partner"
        };
        await createDocApi("logement_bookshelf", dataToSubmit, prefix);
      }

      setIsAddPartnerModalOpen(false);
      setEditingPartner(null);
      await handleFetchData(); // Refresh the list
    } catch (error) {
      console.error("Error saving partner:", error);
      alert("Erreur lors de l'enregistrement de la fiche partenaire");
    } finally {
      setSubmittingPartner(false);
    }
  };

  const handleDeletePartner = (partner: any) => {
    setPartnerToDelete(partner);
  };

  const confirmDeletePartner = async () => {
    if (!partnerToDelete) return;

    setDeletingPartner(partnerToDelete.id);
    try {
      const partnerRef = doc(db, "logement_bookshelf", partnerToDelete.id);
      await deleteDocApi(partnerRef, prefix);

      await handleFetchData();
      setPartnerToDelete(null);
    } catch (error) {
      console.error("Error deleting partner:", error);
      alert("Erreur lors de la suppression de la fiche partenaire");
    } finally {
      setDeletingPartner(null);
    }
  };

  const cancelDeletePartner = () => {
    setPartnerToDelete(null);
  };


  useEffect(() => {
    handleFetchData();
  }, [orgData?.id]);

  useEffect(() => {
    // If active tab is partenaires but org can't create carnets, switch to equipements
    if (activeTab === "partenaires" && orgData?.logement?.access?.can_create !== true) {
      setActiveTab("equipements");
    }
  }, [orgData, activeTab]);

  const transformEquipmentToTable = () => {
    const columns = [
      {
        field: "name",
        headerName: "Nom",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "brand",
        headerName: "Marque",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "model",
        headerName: "Modèle",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "equipment_type",
        headerName: "Type",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "serial_number",
        headerName: "N° de série",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        renderCell: (params: any) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const equipment = equipmentData.find(eq => eq.id === params.row.id);
              handleDeleteEquipment(equipment);
            }}
            disabled={deletingEquipment === params.row.id}
            className="p-1 hover:bg-red-100 rounded-full disabled:opacity-50"
            title="Supprimer l'équipement"
          >
            {deletingEquipment === params.row.id ? (
              <SmallSpinner />
            ) : (
              <Icon1 icon_name="delete" height="16px" width="16px" fill="#ef4444" />
            )}
          </button>
        ),
      },
    ];

    const rows = equipmentData
      .filter(equipment => {
        if (!searchValue) return true;
        const searchLower = searchValue.toLowerCase();
        return (
          equipment.name?.toLowerCase().includes(searchLower) ||
          equipment.brand?.toLowerCase().includes(searchLower) ||
          equipment.model?.toLowerCase().includes(searchLower) ||
          equipment.equipment_type?.toLowerCase().includes(searchLower) ||
          equipment.serial_number?.toLowerCase().includes(searchLower)
        );
      })
      .map(equipment => ({
        id: equipment.id,
        name: equipment.name || "-",
        brand: equipment.brand || "-",
        model: equipment.model || "-",
        equipment_type: equipment.equipment_type || "-",
        serial_number: equipment.serial_number || "-",
      }));

    return { rows, columns };
  };

  const transformPartnerToTable = () => {
    const columns = [
      {
        field: "entreprise",
        headerName: "Entreprise",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "tel",
        headerName: "Téléphone",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "city",
        headerName: "Ville",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "adresse",
        headerName: "Adresse",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "code_postal",
        headerName: "Code Postal",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        renderCell: (params: any) => (
          <div className="flex flex-row gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const partner = partnerData.find(p => p.id === params.row.id);
                handleEditPartner(partner);
              }}
              className="p-1 hover:bg-blue-100 rounded-full"
              title="Modifier la fiche"
            >
              <Icon1 icon_name="edit" height="16px" width="16px" fill="#3b82f6" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const partner = partnerData.find(p => p.id === params.row.id);
                handleDeletePartner(partner);
              }}
              disabled={deletingPartner === params.row.id}
              className="p-1 hover:bg-red-100 rounded-full disabled:opacity-50"
              title="Supprimer la fiche"
            >
              {deletingPartner === params.row.id ? (
                <SmallSpinner />
              ) : (
                <Icon1 icon_name="delete" height="16px" width="16px" fill="#ef4444" />
              )}
            </button>
          </div>
        ),
      },
    ];

    const rows = partnerData
      .filter(partner => {
        if (!searchValue) return true;
        const searchLower = searchValue.toLowerCase();
        return (
          partner.entreprise?.toLowerCase().includes(searchLower) ||
          partner.email?.toLowerCase().includes(searchLower) ||
          partner.tel?.toLowerCase().includes(searchLower) ||
          partner.city?.toLowerCase().includes(searchLower) ||
          partner.adresse?.toLowerCase().includes(searchLower) ||
          partner.code_postal?.toLowerCase().includes(searchLower)
        );
      })
      .map(partner => ({
        id: partner.id,
        entreprise: partner.entreprise || "-",
        email: partner.email || "-",
        tel: partner.tel || "-",
        city: partner.city || "-",
        adresse: partner.adresse || "-",
        code_postal: partner.code_postal || "-",
      }));

    return { rows, columns };
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "equipements":
        if (loading) {
          return (
            <div className="flex flex-col items-center justify-center h-64 gap-2">
              <SmallSpinner />
              <div className="text-h4 text-gray-500">Chargement des équipements...</div>
            </div>
          );
        }

        const { rows, columns } = transformEquipmentToTable();

        return (
          <div className="flex flex-col gap-4 h-full">
            <div className="flex flex-row justify-between items-center">
              <SearchBarDocuments
                orgData={orgData}
                options={equipmentData.map(equipment => ({
                  label: `${equipment.name || ''} - ${equipment.brand || ''} ${equipment.model || ''}`.trim().replace(/^-\s*/, ''),
                  id: equipment.id,
                  equipment: equipment
                }))}
                value={searchValue}
                setValue={setSearchValue}
                onAction={(selectedOption: any) => {
                  if (selectedOption && selectedOption.id) {
                    handleEquipmentClick(selectedOption.id);
                  }
                }}
                setSeachType={(type: any) => {}}
                placeholder="Rechercher un équipement ..."
              />
              <PrimaryButton onClick={handleAddEquipment}>
                <div className="flex flex-row items-center gap-2">
                  <Icon1 icon_name="plus" height="16px" width="16px" fill="white" />
                  Ajouter un équipement
                </div>
              </PrimaryButton>
            </div>
            <div className="text-h3 text-gray-600">
              Ici vous retrouverez tous vos équipements sauvegardés.
            </div>
            {rows.length === 0 ? (
              <div className="flex flex-col gap-2">
                <div className="text-h4 text-gray-500">
                  Aucun équipement sauvegardé pour le moment.
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0">
                <DataGridGPT2
                  rows={rows}
                  columns={columns}
                  searchValue={searchValue}
                  selectable={false}
                  initialPageSize={50}
                  orgData={orgData}
                  onClick={(row: any) => handleEquipmentClick(row.id)}
                />
              </div>
            )}
          </div>
        );

      case "partenaires":
        if (loading) {
          return (
            <div className="flex flex-col items-center justify-center h-64 gap-2">
              <SmallSpinner />
              <div className="text-h4 text-gray-500">Chargement des fiches partenaires...</div>
            </div>
          );
        }

        const partnerTableData = transformPartnerToTable();

        return (
          <div className="flex flex-col gap-4 h-full">
            <div className="flex flex-row justify-between items-center">
              <SearchBarDocuments
                orgData={orgData}
                options={partnerData.map(partner => ({
                  label: `${partner.entreprise || ''} - ${partner.city || ''}`.trim().replace(/^-\s*/, ''),
                  id: partner.id,
                  partner: partner
                }))}
                value={searchValue}
                setValue={setSearchValue}
                onAction={(selectedOption: any) => {
                  if (selectedOption && selectedOption.partner) {
                    handleEditPartner(selectedOption.partner);
                  }
                }}
                setSeachType={(type: any) => {}}
                placeholder="Rechercher une fiche partenaire ..."
              />
              <PrimaryButton onClick={handleAddPartner}>
                <div className="flex flex-row items-center gap-2">
                  <Icon1 icon_name="plus" height="16px" width="16px" fill="white" />
                  Ajouter une fiche
                </div>
              </PrimaryButton>
            </div>
            <div className="text-h3 text-gray-600">
              Ici vous retrouverez toutes vos fiches partenaires sauvegardées.
            </div>
            {partnerTableData.rows.length === 0 ? (
              <div className="flex flex-col gap-2">
                <div className="text-h4 text-gray-500">
                  Aucune fiche partenaire sauvegardée pour le moment.
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0">
                <DataGridGPT2
                  rows={partnerTableData.rows}
                  columns={partnerTableData.columns}
                  searchValue={searchValue}
                  selectable={false}
                  initialPageSize={50}
                  orgData={orgData}
                  onClick={(row: any) => {
                    const partner = partnerData.find(p => p.id === row.id);
                    if (partner) {
                      handleEditPartner(partner);
                    }
                  }}
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto pl-4">
      <div className="flex flex-col h-fit pb-20">
        <div className="flex flex-row items-center gap-3 mb-4">
          <Icon1
            icon_name="bookshelf"
            height="24px"
            width="24px"
          />
          <div className="text-hx text-black font-medium">Bibliothèque</div>
        </div>
        
        <div className="text-h3 text-gray-600 mb-6">
          Gérez votre collection d'équipements et de notices sauvegardés
        </div>

        <div className="flex flex-col gap-4 w-full max-w-c1p2k">
          <TabGPT
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          
          <div className="flex-1 p-4">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Add Equipment Modal */}
      <Modal1 isOpen={isAddEquipmentModalOpen} onClose={() => setIsAddEquipmentModalOpen(false)}>
        <AddEquipmentToBookshelfModal
          onClose={() => setIsAddEquipmentModalOpen(false)}
          onSubmit={handleSubmitEquipment}
          loading={submittingEquipment}
        />
      </Modal1>

      {/* Delete Confirmation Modal */}
      <Modal1 isOpen={!!equipmentToDelete} onClose={cancelDeleteEquipment}>
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex flex-row items-center justify-between mb-4">
            <div className="text-hx font-medium">Confirmer la suppression</div>
            <button
              onClick={cancelDeleteEquipment}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <Icon1 icon_name="close" height="20px" width="20px" />
            </button>
          </div>
          
          <div className="mb-6">
            <div className="text-h3 mb-2">
              Êtes-vous sûr de vouloir supprimer cet équipement ?
            </div>
            {equipmentToDelete && (
              <div className="text-h4 text-gray-600">
                <strong>{equipmentToDelete.name}</strong>
                {equipmentToDelete.brand && ` - ${equipmentToDelete.brand}`}
                {equipmentToDelete.model && ` ${equipmentToDelete.model}`}
              </div>
            )}
            <div className="text-h4 text-red-600 mt-2">
              Cette action est irréversible.
            </div>
          </div>
          
          <div className="flex flex-row gap-2">
            <SecondaryButton 
              onClick={cancelDeleteEquipment}
              className="flex-1"
            >
              Annuler
            </SecondaryButton>
            <PrimaryButton 
              onClick={confirmDeleteEquipment}
              disabled={!!deletingEquipment}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {deletingEquipment ? (
                <div className="flex flex-row items-center gap-2">
                  <SmallSpinner />
                  Suppression...
                </div>
              ) : (
                <div>Supprimer</div>
              )}
            </PrimaryButton>
          </div>
        </div>
      </Modal1>

      {/* Equipment Details Modal */}
      <EquipmentModal
        equipmentId={selectedEquipmentId}
        open={isEquipmentModalOpen}
        onClose={handleCloseEquipmentModal}
        onRefresh={handleRefreshEquipment}
        prefix={prefix}
        isBookshelfMode={true}
        orgId={orgData?.id}
      />

      {/* Add/Edit Partner Modal */}
      <Modal1 isOpen={isAddPartnerModalOpen} onClose={() => {
        setIsAddPartnerModalOpen(false);
        setEditingPartner(null);
      }}>
        <AddPartnerFicheModal
          onClose={() => {
            setIsAddPartnerModalOpen(false);
            setEditingPartner(null);
          }}
          onSubmit={handleSubmitPartner}
          loading={submittingPartner}
          initialData={editingPartner}
          isEdit={!!editingPartner}
          orgId={orgData.id}
        />
      </Modal1>

      {/* Delete Partner Confirmation Modal */}
      <Modal1 isOpen={!!partnerToDelete} onClose={cancelDeletePartner}>
        <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
          <div className="flex flex-row items-center justify-between mb-4">
            <div className="text-hx font-medium">Confirmer la suppression</div>
            <button
              onClick={cancelDeletePartner}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <Icon1 icon_name="close" height="20px" width="20px" />
            </button>
          </div>

          <div className="mb-6">
            <div className="text-h3 mb-2">
              Êtes-vous sûr de vouloir supprimer cette fiche partenaire ?
            </div>
            {partnerToDelete && (
              <div className="text-h4 text-gray-600">
                <strong>{partnerToDelete.entreprise}</strong>
                {partnerToDelete.city && ` - ${partnerToDelete.city}`}
              </div>
            )}
            <div className="text-h4 text-red-600 mt-2">
              Cette action est irréversible.
            </div>
          </div>

          <div className="flex flex-row gap-2">
            <SecondaryButton
              onClick={cancelDeletePartner}
              className="flex-1"
            >
              Annuler
            </SecondaryButton>
            <PrimaryButton
              onClick={confirmDeletePartner}
              disabled={!!deletingPartner}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {deletingPartner ? (
                <div className="flex flex-row items-center gap-2">
                  <SmallSpinner />
                  Suppression...
                </div>
              ) : (
                <div>Supprimer</div>
              )}
            </PrimaryButton>
          </div>
        </div>
      </Modal1>
    </div>
  );
}

export default Bibliotheque;